import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useQueryParam, NumberParam, BooleanParam } from 'use-query-params';
import { useFlags } from 'gatsby-plugin-launchdarkly';

import { Flags } from 'utils/constants/featureFlags';
const { APP_ROOT } = require('utils/constants/urls');
import { COVID_SLUGS } from 'utils/constants/products';
import PageHeading from '../components/PIP/PageHeading';
import PageControlSection from '../components/PIP/PageControlSection';
import ProductDisplayGrid from '../components/PIP/ProductDisplayGrid';
import { Head } from './seo';
import Layout from '../components/Layout';
import { ProductInfoMap, ProductDataType } from 'utils/types';
import { filterUniqueByCategory } from 'utils/helpers';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { TITLES, DESCRIPTIONS } from 'utils/constants/meta';
import { PromoProps } from '../components/PIP/ProductDisplayGrid/PromoCard';

type Props = {
  pageContext: any;
};
export const ProductInfoContext = createContext<ProductInfoMap>({});

const PIPTemplate = (props: Props) => {
  const { pageContext } = props;
  const {
    productCategoryMap,
    productVariants,
    productsBySlug,
    allTests,
    activePromos,
  } = pageContext;

  const [activeCategory, setActiveCategory] = useQueryParam(
    'category',
    NumberParam,
  );

  const flags = useFlags();
  const flagToShowCovidCategory = flags[Flags.CovidPIPCategory];
  const [disableC19Param] = useQueryParam('disableC19', BooleanParam);

  const [productCategoryKeys, setProductCategoryKeys] = useState<Array<string>>(
    Object.keys(productCategoryMap),
  );

  // Array of all tests - contains duplicate entries for products shared between categories
  const showableTests = useMemo(() => allTests.filter((p: ProductDataType) => {
    if (disableC19Param) {
      return !COVID_SLUGS.includes(p.slug);
    } else {
      return true;
    }
  }), []);

  // Array of unique test objects
  const uniqueTests = useMemo(() => showableTests.reduce((unique: ProductDataType[], o: ProductDataType) => {
    if (!unique.some((obj) => obj.productId === o.productId)) {
      unique.push(o);
    }
    return unique;
  }, []), []);

  // Remove Covid from Categories if URL Param present
  useEffect(() => {
    if (flagToShowCovidCategory === false || disableC19Param) {
      setProductCategoryKeys(
        Object.keys(productCategoryMap).filter((cat) => cat !== 'COVID'),
      );
    } else {
      setProductCategoryKeys(Object.keys(productCategoryMap));
    }
  }, [disableC19Param, flagToShowCovidCategory, productCategoryMap]);

  const allCategories = useMemo(
    () => [
      'All Tests',
      ...productCategoryKeys.sort(
        (a: string, b: string) =>
          productCategoryMap[a].order - productCategoryMap[b].order,
      ),
    ],
    [productCategoryKeys],
  );
  const activeCategoryIdx = useRef<number>(0);
  const selectedCategory = useRef<string>('');
  const visiblePromos = useRef<PromoProps[]>([]);
  const categoryPromos = useRef<{ [key: string]: any }>({
    'All Tests': {},
  });
  const [categoryPromo, setCategoryPromo] = useState('');
  const [filterResults, setFilterResults] = useState<ProductDataType[]>([]);



  const handleCategorySelect = (i: number) => {
    /**
     * not best practice to set variables to undefined,
     * but this is how it was described in the docs here:
     * https://github.com/pbeshai/use-query-params#usequeryparam
     **/
    setActiveCategory(undefined);

    if (i > 0) {
      /**
       * Products can belong to multiple categories - we must remove duplicates from the filtered list. This is happening because we need to support category & categories currently.
       */
      const category = allCategories[i];
      const filteredTests = filterUniqueByCategory(showableTests, category);
      setFilterResults(filteredTests);
      trackCategoryFilter(category);
    } else {
      setFilterResults(uniqueTests);
    }
    activeCategoryIdx.current = i;
  };

  const directCategoryLink = (i: number) => {
    /**
     * This method is necessary because of the discrepancy between
     * the category number and the category order/index.
     *
     * also, it's not best practice to set variables to undefined,
     * but this is how it was described in the docs here:
     * https://github.com/pbeshai/use-query-params#usequeryparam
     **/
    setActiveCategory(undefined);
    if (i > 0) {
      const category = productCategoryKeys.find(
        (cat) => productCategoryMap[cat].pipId === i,
      );
      if (!category) return;
      /**
       * Products can belong to multiple categories - we must remove duplicates from the filtered list. This is happening because we need to support category & categories currently.
       */
      const filteredTests = filterUniqueByCategory(showableTests, category);
      setFilterResults(filteredTests);
      activeCategoryIdx.current = allCategories.indexOf(category);
    } else {
      setFilterResults(uniqueTests);
    }
  };

  useEffect(() => {
    categoryPromos.current = Object.keys(productCategoryMap).reduce(
      (acc: any, category) => {
        const { promoActive, promoRow, promoColumn, promoSpan, promo } =
          productCategoryMap[category];
        acc[category] = {
          active: promoActive,
          row: promoRow,
          column: promoColumn,
          span: promoSpan,
          ...promo,
        };
        return acc;
      },
      {},
    );

    if (activeCategory) {
      directCategoryLink(Number(activeCategory));
    } else {
      setFilterResults(uniqueTests);
    }

    trackPageView();
  }, []);

  useEffect(() => {
    if (filterResults.length > 0) {
      selectedCategory.current = allCategories[activeCategoryIdx.current];
      setCategoryPromo(categoryPromos.current[selectedCategory.current]);
    }
  }, [filterResults]);

  useEffect(() => {
    visiblePromos.current =
      activeCategoryIdx.current === 0 ? activePromos : [categoryPromo];
  }, [categoryPromo]);

  return (
    <div data-testid="pip-page">
      <Head
        title={TITLES.PRODUCTS}
        description={DESCRIPTIONS.PRODUCTS}
        url={`${APP_ROOT}/products/`}
      />
      <Layout productsBySlug={productsBySlug}>
        <ProductInfoContext.Provider value={productVariants}>
          <PageHeading />
          <PageControlSection
            handleCategorySelect={handleCategorySelect}
            allTabItems={allCategories}
            categoryIndex={activeCategoryIdx.current}
            numberOfResults={filterResults.length}
          />
          <ProductDisplayGrid
            products={filterResults}
            visiblePromos={visiblePromos.current}
          />
        </ProductInfoContext.Provider>
      </Layout>
    </div>
  );
};

const trackPageView = (): void => {
  ewAnalytics.track({
    event: ANALYTICS.EVENTS.VIEWED_PAGE,
    data: {
      Page: 'PIP',
    },
  });
};

const trackCategoryFilter = (category: string): void => {
  ewAnalytics.track({
    event: ANALYTICS.EVENTS.CLICKED_BUTTON,
    data: {
      label: ANALYTICS.LABELS.PIP_CATEGORY_FILTER,
      category,
    },
  });
}

export default PIPTemplate;
